import { Filter, History } from 'lucide-react';
import { useState } from 'react';
import { Spinner } from '@/components/ui';
import { useEntityHistory } from '@/hooks/use-change-log';
import { cn } from '@/lib/cn';
import { fmtDate as formatDate } from '@/lib/format';

interface ChangeHistoryTabProps {
  loanId: string;
}

const FIELD_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  principal: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  interestRate: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  status: { bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
  maturityDate: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  drawdownDate: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
};

const FIELD_LABELS: Record<string, string> = {
  principal: 'Loan amount',
  interestRate: 'Interest rate',
  defaultRate: 'Default rate',
  maturityDate: 'Maturity date',
  settlementDate: 'Settlement date',
  paymentFrequency: 'Payment frequency',
  termMonths: 'Term (months)',
  'condition.status': 'Condition status',
  'invoice.status': 'Invoice status',
};

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field.replace(/([a-z])([A-Z])/g, '$1 $2');
}

function formatDateValue(value: string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

function formatValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '(empty)';
  const stringValue = String(value);
  if (field === 'interestRate' || field === 'defaultRate') return `${stringValue}%`;
  if (field === 'principal')
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      maximumFractionDigits: 2,
    }).format(Number(value));
  if (field.toLowerCase().includes('date')) return formatDateValue(stringValue) ?? stringValue;
  return stringValue.replace(/_/g, ' ');
}

export function ChangeHistoryTab({ loanId }: ChangeHistoryTabProps) {
  const { data: history, isLoading } = useEntityHistory('LOAN', loanId);
  const [selectedField, setSelectedField] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
        Loading changes…
      </div>
    );
  }

  const changes = history?.changes ?? [];
  const filteredChanges = selectedField
    ? changes.filter((c: any) => c.fieldName === selectedField)
    : changes;
  const uniqueFields = Array.from(
    new Set(changes.map((c: any) => String(c.fieldName))),
  ) as string[];

  return (
    <div className="space-y-4">
      {/* Filter */}
      {uniqueFields.length > 0 && (
        <div className="flex items-center gap-3">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={selectedField || ''}
            onChange={e => setSelectedField(e.target.value || null)}
            className="px-3 py-2 border border-gray-200 rounded-md text-sm text-gray-700 bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All fields</option>
            {uniqueFields.map((field: string) => (
              <option key={field} value={field}>
                {fieldLabel(field)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Timeline */}
      {filteredChanges.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <History className="w-8 h-8 mb-2" />
          <p className="text-sm">No changes found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredChanges.map((change: any, idx: number) => {
            const fieldColor = FIELD_COLORS[change.fieldName] || FIELD_COLORS.status;
            return (
              <div key={idx} className={cn('border border-gray-200 rounded-lg p-4', fieldColor.bg)}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div
                      className={cn('w-2.5 h-2.5 rounded-full mt-1.5 shrink-0', fieldColor.dot)}
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className={cn('text-sm font-medium', fieldColor.text)}>
                        {fieldLabel(change.fieldName)}
                      </h3>
                      <div className="mt-2 space-y-1">
                        <div className="text-xs text-gray-600">
                          <span className="font-medium">Old:</span>{' '}
                          {formatValue(change.fieldName, change.oldValue)}
                        </div>
                        <div className="text-xs text-gray-600">
                          <span className="font-medium">New:</span>{' '}
                          {formatValue(change.fieldName, change.newValue)}
                        </div>
                      </div>
                      {change.reason && (
                        <div className="text-xs text-gray-500 mt-2">
                          <span className="font-medium">Reason:</span> {change.reason}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200/50 text-xs text-gray-500">
                  <span>
                    By{' '}
                    <span className="font-medium">
                      {change.changedBy?.firstName} {change.changedBy?.lastName}
                    </span>
                  </span>
                  <span>{formatDate(change.changedAt)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
